/**
 * Founder Finance — Valuation & Funding Modeler
 *
 * Live scenario modeler for pre-revenue SaaS decisions:
 *   • Baseline valuation breakdown (cost-to-duplicate, market band, comparables)
 *   • SAFE round dilution (amount + cap + discount)
 *   • Priced seed round dilution (pre-money + raise)
 *   • Bootstrap-to-exit projection (MRR growth + exit multiple)
 *   • Side-by-side "net to founder" comparison across exit scenarios
 *
 * All math is client-side. No backend calls. Admin-only route.
 */
import { useState, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Calculator, TrendingUp, DollarSign, PiggyBank, Flame, Info, Package, Sparkles } from "lucide-react";

/* ─────────────────────────── formatting helpers ─────────────────────────── */
const fmtUSD = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};
const fmtPct = (n) => (n == null || Number.isNaN(n) ? "—" : `${(n * 100).toFixed(1)}%`);

/* ─────────────────────────── product asset inventory data ─────────────────────────── */
/**
 * Live inventory of shipped modules — feeds BOTH the ProductAssetInventoryCard
 * AND the cost floor in BaselineCard so the two numbers never drift apart.
 * Updated 2026-02-09 after the Feb sprint: $47 Wholesaling Library Bundle,
 * AI After-Rehab Renderer, Apex 2.0 upgrade, sidebar/dashboard polish, and
 * new-signup alerts. $175/hr = senior FS + product + design blended.
 *
 * Kept in-code (not backend-configurable) so it stays synced with the PRD /
 * CHANGELOG under `/app/memory`.
 */
const PRODUCT_MODULES = [
  {
    group: "Core CRM & Deal Analyzer",
    hours: 420,
    items: [
      "Lead CRM with Kanban + auction leaderboard",
      "AI Deal Analyzer (Apex 2.0, Claude Sonnet 4.5)",
      "Pipeline reports + investor-ready PDFs",
    ],
  },
  {
    group: "Discover · property intake",
    hours: 280,
    items: [
      "Area search (RentCast) + distress filters",
      "Single-address lookup (Feb '26)",
      "Skip-trace this owner (Feb '26)",
      "Text-this-seller SMS w/ TCPA guardrails (Feb '26)",
    ],
  },
  {
    group: "Dispo Studio (Weeks 1–5)",
    hours: 620,
    items: [
      "Deal Rooms + public offer inbox",
      "Buyer signup + match notifications (email/SMS)",
      "Direct photo uploads · emergent storage",
      "Auction leaderboard + auto-counter",
      "Closing Team rolodex + 8-stage pipeline",
      "Kanban Pipeline Board (drag-drop)",
      "Timeline pill on Deal Rooms",
    ],
  },
  {
    group: "Content / top-of-funnel",
    hours: 220,
    items: [
      "12 free playbooks w/ generated PDFs",
      "Back-cover CTAs · ebook survey capture",
      "Legal/TOS gate · consent tracking",
    ],
  },
  {
    group: "Integrations",
    hours: 380,
    items: [
      "Twilio SMS/Voice + A2P 10DLC compliance",
      "Stripe subscriptions + seat billing + webhooks",
      "DocuSign contracts flow",
      "RentCast + BatchData property data",
      "Emergent storage (photos) · Resend email",
    ],
  },
  {
    group: "Admin command center",
    hours: 260,
    items: [
      "Users, Revenue, Funnel, Webhooks diagnostics",
      "Social Media command · Email A/B · Launch Kit",
      "Affiliates · Podcaster partners · VIP links",
      "Marketplace + partner attribution",
    ],
  },
  {
    group: "Auth, billing, ops",
    hours: 250,
    items: [
      "JWT auth · impersonation · audit log",
      "14-day trial · seat-based billing",
      "Feature flags · fast-boot startup · deploy-ready",
    ],
  },
  /* ── Feb '26 sprint (new) ─────────────────────────────────────────────── */
  {
    group: "$47 Wholesaling Library Bundle (Feb '26)",
    hours: 320,
    items: [
      "15-eBook paid bundle w/ Stripe Checkout",
      "Anonymous checkout — no account required",
      "Webhook-driven auto-provisioning + magic-link sign-in",
      "5-layer piracy protection (auth · HMAC signed URLs · TTL · rate limit · watermarks)",
      "In-app paid library UI + \"eBooks are paid-only\" anti-cheat guard",
      "Post-purchase 14-day trial upsell (email + success page)",
      "The Buyer Whisperer eBook (new)",
    ],
  },
  {
    group: "AI · Rehab Renderer + Apex 2.0 v2 (Feb '26)",
    hours: 280,
    items: [
      "AI After-Rehab Renderer · Gemini Nano Banana image-to-image",
      "Deal Room render gallery + object-storage integration",
      "Apex 2.0 · 9-slot deep qualification state machine",
      "ElevenLabs premium TTS w/ voice picker + Pro badge",
      "Hot-lead notifications + auto-callback SMS",
      "TTS homograph pre-processing (\"lead\" → \"leed\")",
      "AI Credits Safety Net · admin health checks · graceful UI degradation",
    ],
  },
  {
    group: "Ops · Signals · UX polish (Feb '26)",
    hours: 150,
    items: [
      "New Signup Alerts (Email + SMS to admins) w/ toggles",
      "Twilio Auto-Bind self-serve UI",
      "Sidebar restructure w/ static Core Workflow + collapsible groups",
      "Today's Activity dashboard strip (4 tiles, deep-linked)",
      "Settings page 2-col masonry layout",
      "Frictionless magic-link post-checkout auth (bcrypt-safe random passwords)",
    ],
  },
];
const BLENDED_RATE = 175;
const TOTAL_SHIPPED_HOURS = PRODUCT_MODULES.reduce((s, g) => s + g.hours, 0);
const REBUILD_COST_FLOOR = TOTAL_SHIPPED_HOURS * BLENDED_RATE;

/* ─────────────────────────── reusable input row ─────────────────────────── */
function NumberField({ label, value, onChange, prefix = "", suffix = "", step = 1, testid }) {
  return (
    <div>
      <Label className="text-xs text-[var(--ink-2)]">{label}</Label>
      <div className="flex items-center gap-1 mt-1">
        {prefix && <span className="text-sm text-[var(--ink-2)]">{prefix}</span>}
        <Input
          type="number"
          value={value}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={testid}
          className="h-9 text-sm"
        />
        {suffix && <span className="text-sm text-[var(--ink-2)]">{suffix}</span>}
      </div>
    </div>
  );
}

/* ─────────────────────────── baseline valuation card ─────────────────────────── */
function BaselineCard({ mrr, valuationOverride, setValuationOverride }) {
  const arr = mrr * 12;
  // Baseline model — refreshed 2026-02-09 to reflect the Feb sprint: $47
  // Wholesaling Library Bundle w/ anonymous checkout + magic-link auto-
  // provisioning, AI After-Rehab Renderer, Apex 2.0 upgrade w/ ElevenLabs,
  // and the sidebar/dashboard polish. Cost floor is computed from
  // PRODUCT_MODULES so the two numbers stay synced.
  const costFloor = REBUILD_COST_FLOOR;
  const midMarket = mrr > 0 ? arr * 8 : 2_400_000;   // 8× ARR OR $2.4M no-rev band (up from $1.8M pre Feb sprint)
  const optimistic = mrr > 0 ? arr * 12 : 3_600_000; // 12× ARR OR $3.6M ceiling with paid users (up from $3.0M)
  const active = valuationOverride ?? midMarket;
  return (
    <Card data-testid="baseline-card" className="border-[var(--line)]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Calculator size={18} className="text-[var(--primary)]" />
          Current baseline valuation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-lg border border-[var(--line)] bg-slate-50">
            <div className="text-xs text-[var(--ink-2)]">Cost floor</div>
            <div className="font-display text-2xl font-bold mt-0.5">{fmtUSD(costFloor)}</div>
            <div className="text-[11px] text-[var(--ink-3)] mt-1">Bottom-up rebuild cost · see Product Asset Inventory ↓</div>
          </div>
          <div className="p-3 rounded-lg border-2 border-[var(--primary)] bg-amber-50">
            <div className="text-xs text-[var(--ink-2)] flex items-center gap-1">
              Fair market <Badge className="bg-[var(--primary)] text-white text-[10px]">CENTER</Badge>
            </div>
            <div className="font-display text-2xl font-bold mt-0.5 text-[var(--primary-dark)]">{fmtUSD(midMarket)}</div>
            <div className="text-[11px] text-[var(--ink-3)] mt-1">
              {mrr > 0 ? `8× ARR (${fmtUSD(arr)})` : "Pre-rev · Dispo Studio + Discover + Apex 2.0 + $47 Bundle live"}
            </div>
          </div>
          <div className="p-3 rounded-lg border border-[var(--line)] bg-slate-50">
            <div className="text-xs text-[var(--ink-2)]">Optimistic band</div>
            <div className="font-display text-2xl font-bold mt-0.5">{fmtUSD(optimistic)}</div>
            <div className="text-[11px] text-[var(--ink-3)] mt-1">
              {mrr > 0 ? "12× ARR w/ strong growth" : "Pre-rev · 10+ paying beta users + retention data"}
            </div>
          </div>
        </div>

        <div className="pt-3 border-t border-[var(--line)]">
          <NumberField
            label="Override — what YOU think the company is worth today"
            value={valuationOverride ?? Math.round(midMarket)}
            onChange={setValuationOverride}
            prefix="$"
            step={50_000}
            testid="valuation-override"
          />
          <div className="text-[11px] text-[var(--ink-3)] mt-1">
            Used as the base for every scenario below · currently {fmtUSD(active)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── product asset inventory ─────────────────────────── */
function ProductAssetInventoryCard() {
  const totalHours = TOTAL_SHIPPED_HOURS;
  const rebuildCost = REBUILD_COST_FLOOR;
  return (
    <Card data-testid="asset-inventory-card" className="border-[var(--line)]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Package size={18} className="text-[var(--primary)]" />
          Product asset inventory
          <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-2">UPDATED FEB 2026</Badge>
        </CardTitle>
        <p className="text-xs text-[var(--ink-2)] mt-1">
          Live list of shipped modules that anchor the cost floor. Show this to any acquirer or angel who asks
          &ldquo;so what have you actually built?&rdquo;
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PRODUCT_MODULES.map((g) => (
            <div
              key={g.group}
              className="rounded-lg border border-[var(--line)] bg-white p-3"
              data-testid={`asset-group-${g.group.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-sm">{g.group}</div>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-[var(--ink-2)] font-semibold">
                  ~{g.hours}h
                </span>
              </div>
              <ul className="space-y-0.5 text-[11px] text-[var(--ink-2)] list-disc pl-4">
                {g.items.map((it) => (
                  <li key={it}>{it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-3 border-t border-[var(--line)] grid grid-cols-3 gap-3">
          <div>
            <div className="text-xs text-[var(--ink-2)]">Total shipped hours</div>
            <div className="font-display text-xl font-bold" data-testid="asset-total-hours">
              ~{totalHours.toLocaleString()}h
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Blended rate</div>
            <div className="font-display text-xl font-bold">${BLENDED_RATE}/h</div>
            <div className="text-[10px] text-[var(--ink-3)]">Senior FS + product + design</div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Bottom-up rebuild cost</div>
            <div className="font-display text-xl font-bold text-[var(--primary-dark)]" data-testid="asset-rebuild-cost">
              {fmtUSD(rebuildCost)}
            </div>
            <div className="text-[10px] text-[var(--ink-3)]">Anchors the cost floor above</div>
          </div>
        </div>

        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900 flex items-start gap-2">
          <Sparkles size={12} className="mt-0.5 shrink-0" />
          <span>
            <b>Why this matters for valuation:</b> a pre-rev SaaS with this many shipped, integrated modules
            (Twilio A2P, Stripe seats, DocuSign, RentCast, live storage, AI Coach) is not comp&apos;d as
            &ldquo;idea stage.&rdquo; The pre-rev midpoint above reflects that reality — you are shipping a
            product an acquirer could operate on day one.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── SAFE round card ─────────────────────────── */
function SafeCard({ founderPct, safeAmount, setSafeAmount, safeCap, setSafeCap, safeDiscount, setSafeDiscount }) {
  // Approximation: SAFE dilution at conversion = amount / valuation_cap
  // (Real dilution depends on the priced round that follows; this is the widely-used pre-round estimate.)
  const dilution = safeCap > 0 ? safeAmount / safeCap : 0;
  const founderAfter = founderPct * (1 - dilution);
  const effectivePrice = safeCap * (1 - safeDiscount / 100);
  return (
    <Card data-testid="safe-card" className="border-[var(--line)]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <PiggyBank size={18} className="text-[var(--primary)]" />
          Scenario A — SAFE round
        </CardTitle>
        <p className="text-xs text-[var(--ink-2)] mt-1">
          Fast, cheap, no valuation debate. Common for angel checks $25K–$500K.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <NumberField label="Raise amount" value={safeAmount} onChange={setSafeAmount} prefix="$" step={25_000} testid="safe-amount" />
          <NumberField label="Valuation cap" value={safeCap} onChange={setSafeCap} prefix="$" step={100_000} testid="safe-cap" />
          <NumberField label="Discount" value={safeDiscount} onChange={setSafeDiscount} suffix="%" testid="safe-discount" />
        </div>
        <div className="pt-3 border-t border-[var(--line)] grid grid-cols-3 gap-3">
          <div>
            <div className="text-xs text-[var(--ink-2)]">Investor dilution</div>
            <div className="font-display text-xl font-bold text-[var(--primary-dark)]">{fmtPct(dilution)}</div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Your % after</div>
            <div className="font-display text-xl font-bold">{fmtPct(founderAfter)}</div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Effective valuation (w/ discount)</div>
            <div className="font-display text-xl font-bold">{fmtUSD(effectivePrice)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Priced Round card ─────────────────────────── */
function PricedCard({ founderPct, pricedAmount, setPricedAmount, preMoney, setPreMoney }) {
  const postMoney = preMoney + pricedAmount;
  const dilution = postMoney > 0 ? pricedAmount / postMoney : 0;
  const founderAfter = founderPct * (1 - dilution);
  return (
    <Card data-testid="priced-card" className="border-[var(--line)]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <TrendingUp size={18} className="text-[var(--primary)]" />
          Scenario B — Priced seed round
        </CardTitle>
        <p className="text-xs text-[var(--ink-2)] mt-1">
          Real valuation is set. Larger raise ($500K–$3M). Board seats usually required.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Raise amount" value={pricedAmount} onChange={setPricedAmount} prefix="$" step={100_000} testid="priced-amount" />
          <NumberField label="Pre-money valuation" value={preMoney} onChange={setPreMoney} prefix="$" step={250_000} testid="priced-premoney" />
        </div>
        <div className="pt-3 border-t border-[var(--line)] grid grid-cols-3 gap-3">
          <div>
            <div className="text-xs text-[var(--ink-2)]">Post-money</div>
            <div className="font-display text-xl font-bold">{fmtUSD(postMoney)}</div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Investor dilution</div>
            <div className="font-display text-xl font-bold text-[var(--primary-dark)]">{fmtPct(dilution)}</div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Your % after</div>
            <div className="font-display text-xl font-bold">{fmtPct(founderAfter)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Bootstrap card ─────────────────────────── */
function BootstrapCard({ mrr, targetMrr, setTargetMrr, monthlyGrowth, setMonthlyGrowth, exitMultiple, setExitMultiple, taxRate }) {
  const months = mrr > 0 && monthlyGrowth > 0
    ? Math.ceil(Math.log(targetMrr / mrr) / Math.log(1 + monthlyGrowth / 100))
    : monthlyGrowth > 0 ? Math.ceil(Math.log(targetMrr / 100) / Math.log(1 + monthlyGrowth / 100)) : 0;
  const targetArr = targetMrr * 12;
  const grossSale = targetArr * exitMultiple;
  const netAfterTax = grossSale * (1 - taxRate);
  return (
    <Card data-testid="bootstrap-card" className="border-[var(--line)]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Flame size={18} className="text-[var(--primary)]" />
          Scenario C — Bootstrap to exit
        </CardTitle>
        <p className="text-xs text-[var(--ink-2)] mt-1">
          No dilution. Grow to a target MRR, then sell. Highest ownership, longest timeline.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <NumberField label="Target MRR" value={targetMrr} onChange={setTargetMrr} prefix="$" step={1_000} testid="bootstrap-target" />
          <NumberField label="Monthly growth" value={monthlyGrowth} onChange={setMonthlyGrowth} suffix="%" step={1} testid="bootstrap-growth" />
          <NumberField label="Exit multiple" value={exitMultiple} onChange={setExitMultiple} suffix="× ARR" step={0.5} testid="bootstrap-multiple" />
        </div>
        <div className="pt-3 border-t border-[var(--line)] grid grid-cols-4 gap-3">
          <div>
            <div className="text-xs text-[var(--ink-2)]">Months to target</div>
            <div className="font-display text-xl font-bold">{months || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Target ARR</div>
            <div className="font-display text-xl font-bold">{fmtUSD(targetArr)}</div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Gross sale price</div>
            <div className="font-display text-xl font-bold text-[var(--primary-dark)]">{fmtUSD(grossSale)}</div>
          </div>
          <div>
            <div className="text-xs text-[var(--ink-2)]">Net after {fmtPct(taxRate)} tax</div>
            <div className="font-display text-xl font-bold" style={{ color: "#10B981" }}>{fmtUSD(netAfterTax)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── comparison card ─────────────────────────── */
function ComparisonCard({ founderPct, safe, priced, bootstrap, taxRate }) {
  const rows = useMemo(() => {
    // Exit scenarios: $2M, $5M, $10M sale
    const exits = [2_000_000, 5_000_000, 10_000_000, bootstrap.grossSale];
    return exits.map((exit) => {
      const bootstrapNet = bootstrap.grossSale === exit
        ? bootstrap.grossSale * (1 - taxRate)
        : exit * (1 - taxRate); // 100% founder ownership × exit × (1-tax)
      const safeNet = exit * (founderPct * (1 - safe.dilution)) * (1 - taxRate);
      const pricedNet = exit * (founderPct * (1 - priced.dilution)) * (1 - taxRate);
      return { exit, bootstrapNet, safeNet, pricedNet };
    });
  }, [founderPct, safe.dilution, priced.dilution, bootstrap.grossSale, taxRate]);
  return (
    <Card data-testid="comparison-card" className="border-[var(--primary)] border-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <DollarSign size={18} className="text-[var(--primary)]" />
          Side-by-side — net to founder at each exit price
        </CardTitle>
        <p className="text-xs text-[var(--ink-2)] mt-1">
          Same exit price, different paths. Higher = better outcome for you personally (after tax).
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="comparison-table">
            <thead>
              <tr className="text-left text-xs text-[var(--ink-2)] border-b border-[var(--line)]">
                <th className="pb-2">Exit price</th>
                <th className="pb-2">Bootstrap (100% you)</th>
                <th className="pb-2">SAFE path</th>
                <th className="pb-2">Priced seed path</th>
                <th className="pb-2">Best path</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const best = Math.max(r.bootstrapNet, r.safeNet, r.pricedNet);
                const bestLabel = best === r.bootstrapNet ? "Bootstrap" : best === r.safeNet ? "SAFE" : "Priced";
                return (
                  <tr key={i} className="border-b border-[var(--line)] last:border-0">
                    <td className="py-2 font-semibold">{fmtUSD(r.exit)}</td>
                    <td className={r.bootstrapNet === best ? "py-2 font-bold text-[var(--primary-dark)]" : "py-2"}>{fmtUSD(r.bootstrapNet)}</td>
                    <td className={r.safeNet === best ? "py-2 font-bold text-[var(--primary-dark)]" : "py-2"}>{fmtUSD(r.safeNet)}</td>
                    <td className={r.pricedNet === best ? "py-2 font-bold text-[var(--primary-dark)]" : "py-2"}>{fmtUSD(r.pricedNet)}</td>
                    <td className="py-2 text-[var(--primary-dark)] font-semibold">{bestLabel}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-[var(--ink-2)]">
          <Info size={14} className="mt-0.5 text-[var(--primary-dark)]" />
          <div>
            <strong className="text-[var(--ink)]">Reality check:</strong> Bootstrap wins the math but loses the timing.
            SAFE/priced money buys speed — hire, market, and hit that exit 12–24 months earlier. Time value of money
            + market timing risk often flips the math in favor of raising. Model both, then trust your gut.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── main page ─────────────────────────── */
export default function AdminValuation() {
  const [mrr, setMrr] = useState(0);
  const [founderPct] = useState(1.0); // 100% today, no cap table yet
  const [valuationOverride, setValuationOverride] = useState(null);
  const [taxRate, setTaxRate] = useState(0.28);

  // SAFE
  const [safeAmount, setSafeAmount] = useState(250_000);
  const [safeCap, setSafeCap] = useState(3_000_000);
  const [safeDiscount, setSafeDiscount] = useState(20);
  const safe = { dilution: safeCap > 0 ? safeAmount / safeCap : 0 };

  // Priced
  const [pricedAmount, setPricedAmount] = useState(750_000);
  const [preMoney, setPreMoney] = useState(2_500_000);
  const priced = { dilution: (preMoney + pricedAmount) > 0 ? pricedAmount / (preMoney + pricedAmount) : 0 };

  // Bootstrap
  const [targetMrr, setTargetMrr] = useState(50_000);
  const [monthlyGrowth, setMonthlyGrowth] = useState(15);
  const [exitMultiple, setExitMultiple] = useState(6);
  const bootstrap = { grossSale: targetMrr * 12 * exitMultiple };

  return (
    <div className="space-y-4" data-testid="admin-valuation">
      <Card className="border-[var(--line)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <DollarSign size={18} className="text-[var(--primary)]" />
            Your inputs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            <NumberField label="Current MRR" value={mrr} onChange={setMrr} prefix="$" step={100} testid="input-mrr" />
            <div>
              <Label className="text-xs text-[var(--ink-2)]">Effective tax rate</Label>
              <div className="mt-2">
                <Slider
                  value={[taxRate * 100]}
                  onValueChange={(v) => setTaxRate(v[0] / 100)}
                  min={0} max={50} step={1}
                  data-testid="input-tax-rate"
                />
                <div className="text-xs mt-1 text-[var(--ink-2)]">{fmtPct(taxRate)} · federal + state combined on gains</div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[var(--ink-2)]">Your ownership today</Label>
              <div className="font-display text-2xl font-bold mt-1">{fmtPct(founderPct)}</div>
              <div className="text-[11px] text-[var(--ink-3)]">Solo founder, no cap table yet</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <BaselineCard mrr={mrr} valuationOverride={valuationOverride} setValuationOverride={setValuationOverride} />

      <ProductAssetInventoryCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SafeCard
          founderPct={founderPct}
          safeAmount={safeAmount} setSafeAmount={setSafeAmount}
          safeCap={safeCap} setSafeCap={setSafeCap}
          safeDiscount={safeDiscount} setSafeDiscount={setSafeDiscount}
        />
        <PricedCard
          founderPct={founderPct}
          pricedAmount={pricedAmount} setPricedAmount={setPricedAmount}
          preMoney={preMoney} setPreMoney={setPreMoney}
        />
      </div>

      <BootstrapCard
        mrr={mrr}
        targetMrr={targetMrr} setTargetMrr={setTargetMrr}
        monthlyGrowth={monthlyGrowth} setMonthlyGrowth={setMonthlyGrowth}
        exitMultiple={exitMultiple} setExitMultiple={setExitMultiple}
        taxRate={taxRate}
      />

      <ComparisonCard
        founderPct={founderPct}
        safe={safe}
        priced={priced}
        bootstrap={bootstrap}
        taxRate={taxRate}
      />
    </div>
  );
}
